import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ErpSyncSummary, ErpSyncVendor } from './erp-sync-progress.types';

const NOTHING = 0;

/**
 * The pull while it runs, and what it did when it finished — a pure view.
 *
 * Two states in ONE component rather than two, because they are two frames of the same event and the
 * user watches one turn into the other. Splitting them would put the vendor logo, the period and the
 * modal size in two places that have to agree.
 *
 * The visual language is the annual report's ERP import: vendor logo, arrow, aumico logo, an
 * indeterminate bar. Deliberately the same, because it is the same act from the user's side — only
 * the destination differs. Those modals still carry their own copy of this markup; a shared component
 * is the follow-up, and this is where it should end up.
 *
 * A running pull cannot be dismissed: there is no cancel on the route, so a close button would hide a
 * request that keeps writing. The summary can be dismissed, because by then nothing is in flight.
 */
@Component({
    selector: 'abcp-erp-sync-progress',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './erp-sync-progress.component.html',
    styleUrls: ['./erp-sync-progress.component.scss'],
})
export class ErpSyncProgressComponent {
    /** `loading` while the pull runs, `done` once it has finished. */
    @Input() state: 'loading' | 'done' = 'loading';

    /** The system being pulled from. Null falls back to the aumico mark alone. */
    @Input() vendor: ErpSyncVendor | null = null;

    /** What the pull did. Null while it is still running. */
    @Input() summary: ErpSyncSummary | null = null;

    @Output() closed = new EventEmitter<void>();

    get isLoading(): boolean {
        return this.state === 'loading';
    }

    /** Whether any comparison period failed — the one outcome worth pointing at. */
    get hasFailure(): boolean {
        return (this.summary?.comparisonPeriods ?? []).some((period) => period.failed);
    }

    get hasComparisonPeriods(): boolean {
        return (this.summary?.comparisonPeriods.length ?? NOTHING) > NOTHING;
    }

    onClose(): void {
        this.closed.emit();
    }
}
